/**
 * Canonical DOM predicates about an element's "editability".
 *
 * PATTERN TO FOLLOW: the question "is this element editable?" is answered ONLY
 * from here. Previously this logic (`TAGS.has(tagName) || isContentEditable`)
 * was copied in `lib/gestures/guards`, `lib/interaction/context`,
 * `components/ui/dialog` and `todo-detail.helpers` with four slightly
 * different variants, at risk of diverging. Import these helpers instead of rewriting
 * the check.
 *
 * There are two predicates with DISTINCT semantics — choose according to the intent:
 *  - `isEditableElement`: any form control (includes SELECT,
 *    checkbox…) or `contenteditable`. Use it to *suppress* global gestures/shortcuts
 *    when the focus is inside a control.
 *  - `isTextEntryElement`: ONLY free text entry (textarea or textual input
 *    type). Stricter. Use it for decisions based on the text
 *    cursor, e.g. not stealing the arrows while editing text.
 *
 * Note: there are one-off checks (e.g. in the detail's key handling) that
 * deliberately use their own tag sets (exclude SELECT, include BUTTON/A…)
 * because they ask something different ("does the control consume this key?"). Those
 * must NOT be collapsed here: the different semantics are intentional.
 */

/** Form controls that count as "editable". */
const FORM_CONTROL_TAGS = new Set(["INPUT", "TEXTAREA", "SELECT"]);

/**
 * `true` if `el` is a form control (input/textarea/select) or a
 * `contenteditable` element.
 */
export function isEditableElement(el: Element | null): el is HTMLElement {
  return el instanceof HTMLElement && (FORM_CONTROL_TAGS.has(el.tagName) || el.isContentEditable);
}

/**
 * `true` if there is any editable field with focus, or any
 * `contenteditable` exists in the document. Intended for global gates (dragging
 * dialogs, navigation swipe) that must yield to editing. SSR-safe.
 */
export function isEditingActive(): boolean {
  if (typeof document === "undefined") {
    return false;
  }
  if (document.querySelector('[contenteditable="true"], [contenteditable=""]') !== null) {
    return true;
  }
  return isEditableElement(document.activeElement);
}
